import { useId, type JSX } from 'react'
import { useFormContext } from 'react-hook-form'
import { InputText } from 'primereact/inputtext'

interface Props {
  name: string
  label: string
  helpText?: string
  placeholder?: string
  type?: 'text' | 'email' | 'password' | 'tel' | 'url'
  autoFocus?: boolean
  disabled?: boolean
  required?: boolean
}

export function FormText({ name, label, helpText, type = 'text', required, ...rest }: Props): JSX.Element {
  const { register, formState: { errors } } = useFormContext()
  const error = errors[name]
  const errorMessage = typeof error?.message === 'string' ? error.message : undefined
  const id = useId()
  const errorId = `${id}-error`
  const helpId = `${id}-help`
  const describedBy = [helpText && helpId, errorMessage && errorId].filter(Boolean).join(' ') || undefined

  return (
    <div className="flex flex-col gap-1.5 mb-4">
      <label htmlFor={id} className="text-sm font-medium text-gray-700">
        {label}{required && <span aria-hidden className="text-red-500 ml-1">*</span>}
      </label>
      <InputText
        id={id}
        type={type}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={describedBy}
        {...register(name)}
        {...rest}
        pt={{
          root: { className: 'w-full' },
          input: { className: 'w-full rounded-lg border border-gray-200 bg-white py-2.5 px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#16273F] focus:ring-2 focus:ring-[#16273F]/20 focus:outline-none transition-all shadow-sm' }
        } as any}
      />
      {errorMessage && (
        <small id={errorId} role="alert" className="text-red-600 text-xs font-medium">{errorMessage}</small>
      )}
      {helpText && <small id={helpId} className="text-gray-500 text-xs">{helpText}</small>}
    </div>
  )
}
